import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/lib/hooks/use-billing";
import { useBillingPortal } from "@/lib/hooks/use-billing-portal";
import { useUserRole } from "@/lib/rbac/hooks";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { SubscriptionStatus } from "@/lib/graphql/types-and-hooks";

/**
 * Global banner shown when the workspace subscription is in `past_due` —
 * Stripe is still retrying the charge, so we keep the user in the app and
 * surface the failed payment here (non-dismissible by design). Admins get a
 * CTA into the Stripe payment-method-update flow; members are pointed at
 * their workspace admin.
 */
export default function PastDuePaymentBanner() {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { subscription } = useBilling();
  const { openBillingPortal } = useBillingPortal();
  const role = useUserRole();

  if (!subscription || subscription.status !== SubscriptionStatus.PastDue) return null;

  const isAdmin = role === "admin" || role === "owner";
  const periodEnd = subscription.currentPeriodEnd;

  return (
    <div className="relative w-full shrink-0 border-b border-destructive/30 bg-destructive/10 px-4 py-2">
      <div className="flex items-center justify-center gap-3 text-xs text-foreground">
        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
        {isAdmin ? (
          <>
            <p>
              <span className="font-semibold">{t("banners.pastDueTitle")}</span>{" "}
              {periodEnd
                ? t("banners.pastDueAdminByDate", { date: formatDate(periodEnd, "long") })
                : t("banners.pastDueAdmin")}
            </p>
            <button
              onClick={() => openBillingPortal({ flowData: { type: "payment_method_update" } })}
              className="font-semibold text-destructive underline underline-offset-2 hover:opacity-80 transition shrink-0"
            >
              {t("banners.pastDueUpdatePayment")}
            </button>
          </>
        ) : (
          <p>
            <span className="font-semibold">{t("banners.pastDueTitle")}</span>{" "}
            {t("banners.pastDueMember")}
          </p>
        )}
      </div>
    </div>
  );
}
